import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FaPlus, FaUserPlus } from "react-icons/fa";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import {
  useGetServicesQuery,
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateServiceMutation,
} from "../apis/services";
import { useGetOfficesQuery } from "apis/office";
import ProviderAvailability from "components/services/ProviderAvailability";
import {
  toAudienceValue,
  fromAudienceValue,
  validateAudience,
} from "components/products/AudienceSelect";
import ProviderFields from "components/services/ProviderFields";
import Table from "components/ui/Table";

const Services = () => {
  const pageSize = 10;

  // Modal states
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isEditProviderOpen, setIsEditProviderOpen] = useState(false);
  const [isAddProviderOpen, setIsAddProviderOpen] = useState(false);
  const [isDeleteProviderOpen, setIsDeleteProviderOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isDeleteCategoryOpen, setIsDeleteCategoryOpen] = useState(false);

  // Form states
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState("");
  const [selectedCategoryForDelete, setSelectedCategoryForDelete] = useState("");
  const [newCategory, setNewCategory] = useState({
    category: "",
    providers: [{ name: "", phone: "", email: "" }],
  });
  const [editProvider, setEditProvider] = useState({
    name: "",
    phone: "",
    email: "",
    audience: { mode: "all", offices: [] },
  });
  const [newProviders, setNewProviders] = useState([{ name: "", phone: "", email: "" }]);
  const [editCategoryName, setEditCategoryName] = useState("");

  const [newCategoryAudience, setNewCategoryAudience] = useState({
    mode: "all",
    offices: [],
  });
  const [newProvidersAudience, setNewProvidersAudience] = useState({
    mode: "all",
    offices: [],
  });

  // Validation states
  const [categoryErrors, setCategoryErrors] = useState({});
  const [editProviderErrors, setEditProviderErrors] = useState({});
  const [addProviderErrors, setAddProviderErrors] = useState({});
  const [editCategoryErrors, setEditCategoryErrors] = useState({});

  // API hooks
  const { data: servicesData, isLoading } = useGetServicesQuery();
  const { data: offices = [] } = useGetOfficesQuery();
  const { mutate: addCategory, isLoading: isAddingCategory } = useAddCategoryMutation();
  const { mutate: deleteCategory, isLoading: isDeletingCategory } = useDeleteCategoryMutation();
  const { mutate: updateService, isLoading: isUpdatingService } = useUpdateServiceMutation();

  const officeNameById = useMemo(() => {
    const map = new Map();
    offices.forEach((office) => {
      map.set(String(office._id), office.name);
    });
    return map;
  }, [offices]);

  // Transform data for table
  const tableData = useMemo(() => {
    if (!servicesData) return [];

    const flatData = [];
    servicesData.forEach((categoryGroup) => {
      categoryGroup.providers.forEach((provider) => {
        flatData.push({
          ...provider,
          category: categoryGroup.category,
          officeNames: provider.offices?.length
            ? provider.offices
                .map(
                  (office) =>
                    officeNameById.get(String(office?._id || office)) ||
                    "Unknown"
                )
                .join(", ")
            : "All offices",
        });
      });
    });
    return flatData;
  }, [servicesData, officeNameById]);

  const categoriesData = useMemo(() => {
    if (!servicesData) return [];
    return servicesData.map((categoryGroup) => ({
      category: categoryGroup.category,
      providerCount: categoryGroup.providers.length,
    }));
  }, [servicesData]);

  const serviceByCategory = useMemo(() => {
    const map = new Map();
    (servicesData || []).forEach((group) => {
      const id = group._id || group.id;
      map.set(group.category, {
        id,
        providers: group.providers,
        category: group.category,
      });
    });
    return map;
  }, [servicesData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "category",
        header: "Category",
        isFilter: true,
      },
      {
        accessorKey: "officeNames",
        header: "Offices",
        isFilter: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        accessorKey: "name",
        header: "Provider Name",
        isFilter: true,
      },
      {
        accessorKey: "phone",
        header: "Phone Number",
        cell: (row) => row.phone || "-",
        isFilter: true,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (row) => row.email || "-",
        isFilter: true,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProvider(row);
                setEditProvider({
                  name: row.name,
                  phone: row.phone || "",
                  email: row.email || "",
                  audience: toAudienceValue(row.offices),
                });
                setEditProviderErrors({});
                setIsEditProviderOpen(true);
              }}
              className="edit-icon"
              title="Edit Provider"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProvider(row);
                setIsDeleteProviderOpen(true);
              }}
              className="delete-icon"
              title="Delete Provider"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    []
  );

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAddCategory = () => {
    const errors = {};
    
    if (!newCategory.category.trim()) {
      errors.category = "Category name is required";
    }

    const providerErrors = [];
    newCategory.providers.forEach((provider, index) => {
      const pErrors = {};
      
      if (!provider.name.trim()) {
        pErrors.name = "Provider name is required";
      }
      
      if (!provider.phone.trim() && !provider.email.trim()) {
        pErrors.contact = "Either phone or email is required";
      }

      if (provider.email && !validateEmail(provider.email)) {
        pErrors.email = "Invalid email format";
      }

      if (Object.keys(pErrors).length > 0) {
        providerErrors[index] = pErrors;
      }
    });

    if (providerErrors.length > 0 || Object.keys(providerErrors).length > 0) {
      errors.providers = providerErrors;
    }

    const audienceError = validateAudience(newCategoryAudience);
    if (audienceError) {
      errors.audience = audienceError;
    }

    setCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditProvider = () => {
    const errors = {};

    if (!editProvider.name.trim()) {
      errors.name = "Provider name is required";
    }

    if (!editProvider.phone.trim() && !editProvider.email.trim()) {
      errors.contact = "Either phone or email is required";
    }

    if (editProvider.email && !validateEmail(editProvider.email)) {
      errors.email = "Invalid email format";
    }

    const audienceError = validateAudience(editProvider.audience);
    if (audienceError) {
      errors.audience = audienceError;
    }

    setEditProviderErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAddProviders = () => {
    const errors = {};

    if (!selectedCategory) {
      errors.category = "Please select a category";
    }

    const providerErrors = [];
    newProviders.forEach((provider, index) => {
      const pErrors = {};

      if (!provider.name.trim()) {
        pErrors.name = "Provider name is required";
      }

      if (!provider.phone.trim() && !provider.email.trim()) {
        pErrors.contact = "Either phone or email is required";
      }

      if (provider.email && !validateEmail(provider.email)) {
        pErrors.email = "Invalid email format";
      }

      if (Object.keys(pErrors).length > 0) {
        providerErrors[index] = pErrors;
      }
    });

    if (providerErrors.length > 0 || Object.keys(providerErrors).length > 0) {
      errors.providers = providerErrors;
    }

    const audienceError = validateAudience(newProvidersAudience);
    if (audienceError) {
      errors.audience = audienceError;
    }

    setAddProviderErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditCategory = () => {
    const errors = {};

    if (!editCategoryName.trim()) {
      errors.category = "Category name is required";
    }

    setEditCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submissions
  const handleAddCategory = () => {
    if (!validateAddCategory()) return;

    addCategory(
      {
        ...newCategory,
        providers: newCategory.providers.map((provider) => ({
          ...provider,
          offices: fromAudienceValue(newCategoryAudience),
        })),
      },
      {
        onSuccess: () => {
          setIsAddCategoryOpen(false);
          setNewCategory({
            category: "",
            providers: [{ name: "", phone: "", email: "" }],
          });
          setNewCategoryAudience({ mode: "all", offices: [] });
          setCategoryErrors({});
        },
      }
    );
  };

  const handleUpdateProvider = () => {
    if (!validateEditProvider()) return;

    const service = serviceByCategory.get(selectedProvider.category);
    if (service?.id) {
      const updatedProviders = (service.providers || []).map((p) =>
        p._id === selectedProvider._id
          ? {
              ...p,
              name: editProvider.name,
              phone: editProvider.phone,
              email: editProvider.email,
              offices: fromAudienceValue(editProvider.audience),
            }
          : p
      );
      updateService(
        {
          id: service.id,
          category: service.category,
          providers: updatedProviders,
        },
        {
          onSuccess: () => {
            setIsEditProviderOpen(false);
            setSelectedProvider(null);
            setEditProvider({
              name: "",
              phone: "",
              email: "",
              audience: { mode: "all", offices: [] },
            });
            setEditProviderErrors({});
          },
        }
      );
    }
  };

  const handleAddProviders = () => {
    if (!validateAddProviders()) return;

    const service = serviceByCategory.get(selectedCategory);
    if (service?.id) {
      const updatedProviders = [
        ...(service.providers || []),
        ...newProviders.map((provider) => ({
          ...provider,
          offices: fromAudienceValue(newProvidersAudience),
        })),
      ];
      updateService(
        {
          id: service.id,
          category: service.category,
          providers: updatedProviders,
        },
        {
          onSuccess: () => {
            setIsAddProviderOpen(false);
            setNewProviders([{ name: "", phone: "", email: "" }]);
            setSelectedCategory("");
            setNewProvidersAudience({ mode: "all", offices: [] });
            setAddProviderErrors({});
          },
        }
      );
    }
  };

  const handleDeleteProvider = () => {
    if (selectedProvider) {
      const service = serviceByCategory.get(selectedProvider.category);
      if (service?.id) {
        const updatedProviders = (service.providers || []).filter(
          (p) => p._id !== selectedProvider._id
        );
        updateService(
          {
            id: service.id,
            category: service.category,
            providers: updatedProviders,
          },
          {
            onSuccess: () => {
              setIsDeleteProviderOpen(false);
              setSelectedProvider(null);
            },
          }
        );
      }
    }
  };

  const handleUpdateCategory = () => {
    if (!validateEditCategory()) return;

    const service = serviceByCategory.get(selectedCategoryForEdit);
    const newCat = editCategoryName.toUpperCase();
    if (service?.id) {
      updateService(
        {
          id: service.id,
          category: newCat,
          providers: service.providers || [],
        },
        {
          onSuccess: () => {
            setIsEditCategoryOpen(false);
            setSelectedCategoryForEdit("");
            setEditCategoryName("");
            setEditCategoryErrors({});
          },
        }
      );
    }
  };

  const handleDeleteCategory = () => {
    if (selectedCategoryForDelete) {
      const service = serviceByCategory.get(selectedCategoryForDelete);

      deleteCategory(
        {
          category: service?.id,
        },
        {
          onSuccess: () => {
            setIsDeleteCategoryOpen(false);
            setSelectedCategoryForDelete("");
          },
        }
      );
    }
  };

  const uniqueCategories = useMemo(() => {
    return [...new Set(tableData.map((item) => item.category))];
  }, [tableData]);

  // Helper function for input class with validation
  const getInputClass = (hasError) => {
    return `form-input border ${hasError ? "border-red-500" : "border-mediumgray"}`;
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="container mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => {
              setIsAddCategoryOpen(true);
              setCategoryErrors({});
            }}
            className="black-button flex items-center gap-2"
          >
            <FaPlus className="h-4 w-4" />
            Add Category
          </Button>
          <Button
            onClick={() => {
              setIsAddProviderOpen(true);
              setAddProviderErrors({});
            }}
            className="outline-button flex items-center gap-2"
          >
            <FaUserPlus className="h-4 w-4" />
            Add Provider
          </Button>
        </div>
      </div>
      <div className="my-6">
        <h2>Categories Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categoriesData.map((categoryData) => (
            <div
              key={categoryData.category}
              className="bg-lightergray p-4 rounded-lg border border-mediumgray"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-darkergray">
                    {categoryData.category}
                  </h3>
                  <p className="text-sm text-darkgray">
                    {categoryData.providerCount} provider(s)
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedCategoryForEdit(categoryData.category);
                      setEditCategoryName(categoryData.category);
                      setEditCategoryErrors({});
                      setIsEditCategoryOpen(true);
                    }}
                    className="view-icon text-darkergray hover:text-black"
                    title="Edit Category"
                  >
                    <FiEdit2 fontSize={16} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCategoryForDelete(categoryData.category);
                      setIsDeleteCategoryOpen(true);
                    }}
                    className="view-icon text-red-600 hover:text-red-800"
                    title="Delete Category"
                  >
                    <FiTrash2 fontSize={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <Table
          title="Providers List"
          columns={columns}
          data={tableData || []}
          pageSize={pageSize}
        />
      </div>

      {/* Add Category Modal */}
      <Modal
        title="Add New Category"
        isOpen={isAddCategoryOpen}
        onClose={() => {
          setIsAddCategoryOpen(false);
          setCategoryErrors({});
        }}
        className="max-w-2xl"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Category Name *</label>
            <input
              type="text"
              value={newCategory.category}
              onChange={(e) =>
                setNewCategory({
                  ...newCategory,
                  category: e.target.value.toUpperCase(),
                })
              }
              className={getInputClass(categoryErrors.category)}
              placeholder="e.g., PLUMBING, GARDENING"
            />
            {categoryErrors.category && (
              <p className="text-red-500 text-sm mt-1">{categoryErrors.category}</p>
            )}
          </div>

          <ProviderAvailability
            value={newCategoryAudience}
            onChange={setNewCategoryAudience}
            error={categoryErrors.audience}
          />

          <ProviderFields
            providers={newCategory.providers}
            errors={categoryErrors.providers}
            onChange={(providers) =>
              setNewCategory({ ...newCategory, providers })
            }
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={() => {
              setIsAddCategoryOpen(false);
              setCategoryErrors({});
            }}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddCategory}
            className="black-button"
            disabled={isAddingCategory}
            loading={isAddingCategory}
          >
            {isAddingCategory ? "Adding..." : "Add Category"}
          </Button>
        </div>
      </Modal>

      {/* Edit Provider Modal */}
      <Modal
        title="Edit Provider"
        isOpen={isEditProviderOpen}
        onClose={() => {
          setIsEditProviderOpen(false);
          setEditProviderErrors({});
        }}
        className="max-w-2xl"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Provider Name *</label>
            <input
              type="text"
              value={editProvider.name}
              onChange={(e) =>
                setEditProvider({ ...editProvider, name: e.target.value })
              }
              className={getInputClass(editProviderErrors.name)}
            />
            {editProviderErrors.name && (
              <p className="text-red-500 text-sm mt-1">{editProviderErrors.name}</p>
            )}
          </div>
          <div>
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              value={editProvider.phone}
              onChange={(e) =>
                setEditProvider({ ...editProvider, phone: e.target.value })
              }
              className={getInputClass(editProviderErrors.contact)}
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="form-label">Email</label>
            <input
              type="email"
              value={editProvider.email}
              onChange={(e) =>
                setEditProvider({ ...editProvider, email: e.target.value })
              }
              className={getInputClass(editProviderErrors.contact || editProviderErrors.email)}
              placeholder="provider@example.com"
            />
            {editProviderErrors.contact && (
              <p className="text-red-500 text-sm mt-1">{editProviderErrors.contact}</p>
            )}
            {editProviderErrors.email && (
              <p className="text-red-500 text-sm mt-1">{editProviderErrors.email}</p>
            )}
          </div>
          <ProviderAvailability
            value={editProvider.audience}
            onChange={(audience) =>
              setEditProvider({ ...editProvider, audience })
            }
            error={editProviderErrors.audience}
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={() => {
              setIsEditProviderOpen(false);
              setEditProviderErrors({});
            }}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateProvider}
            className="black-button"
            disabled={isUpdatingService}
            loading={isUpdatingService}
          >
            {isUpdatingService ? "Updating..." : "Update Provider"}
          </Button>
        </div>
      </Modal>

      {/* Add Provider Modal */}
      <Modal
        title="Add Providers"
        isOpen={isAddProviderOpen}
        onClose={() => {
          setIsAddProviderOpen(false);
          setAddProviderErrors({});
        }}
        className="max-w-3xl"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Select Category *</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`form-select ${addProviderErrors.category ? "border-red-500" : "border-mediumgray"} border`}
            >
              <option value="">Select a category</option>
              {uniqueCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            {addProviderErrors.category && (
              <p className="text-red-500 text-sm mt-1">{addProviderErrors.category}</p>
            )}
          </div>

          <ProviderAvailability
            value={newProvidersAudience}
            onChange={setNewProvidersAudience}
            error={addProviderErrors.audience}
          />

          <ProviderFields
            providers={newProviders}
            errors={addProviderErrors.providers}
            onChange={setNewProviders}
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={() => {
              setIsAddProviderOpen(false);
              setAddProviderErrors({});
            }}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddProviders}
            className="black-button"
            disabled={isUpdatingService}
            loading={isUpdatingService}
          >
            {isUpdatingService ? "Adding..." : "Add Providers"}
          </Button>
        </div>
      </Modal>

      {/* Delete Provider Modal */}
      <Modal
        title="Delete Provider"
        isOpen={isDeleteProviderOpen}
        onClose={() => setIsDeleteProviderOpen(false)}
      >
        <p className="text-darkgray mb-6">
          Are you sure you want to delete{" "}
          <strong>{selectedProvider?.name}</strong> from{" "}
          <strong>{selectedProvider?.category}</strong> category? This action
          cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => setIsDeleteProviderOpen(false)}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteProvider}
            className="black-button bg-red-600 hover:bg-red-700"
            disabled={isUpdatingService}
            loading={isUpdatingService}
          >
            {isUpdatingService ? "Deleting..." : "Delete Provider"}
          </Button>
        </div>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        title="Edit Category"
        isOpen={isEditCategoryOpen}
        onClose={() => {
          setIsEditCategoryOpen(false);
          setEditCategoryErrors({});
        }}
        className="max-w-lg"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Category Name *</label>
            <input
              type="text"
              value={editCategoryName}
              onChange={(e) =>
                setEditCategoryName(e.target.value.toUpperCase())
              }
              className={getInputClass(editCategoryErrors.category)}
              placeholder="Enter new category name"
            />
            {editCategoryErrors.category && (
              <p className="text-red-500 text-sm mt-1">{editCategoryErrors.category}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            onClick={() => {
              setIsEditCategoryOpen(false);
              setEditCategoryErrors({});
            }}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCategory}
            className="black-button"
            disabled={isUpdatingService}
            loading={isUpdatingService}
          >
            {isUpdatingService ? "Updating..." : "Update Category"}
          </Button>
        </div>
      </Modal>

      {/* Delete Category Modal */}
      <Modal
        title="Delete Category"
        isOpen={isDeleteCategoryOpen}
        onClose={() => setIsDeleteCategoryOpen(false)}
      >
        <p className="text-darkgray mb-6">
          Are you sure you want to delete the{" "}
          <strong>{selectedCategoryForDelete}</strong> category? This will
          permanently remove the category and all its providers. This action
          cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => setIsDeleteCategoryOpen(false)}
            className="gray-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCategory}
            className="black-button bg-red-600 hover:bg-red-700"
            disabled={isDeletingCategory}
            loading={isDeletingCategory}
          >
            {isDeletingCategory ? "Deleting..." : "Delete Category"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Services;